import { useCallback, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { ActionWarning } from '@renderer/components/action-warning'
import { useStagedActionEditOptions, type StagedAction } from '../../../hooks/use-staged-actions'
import { isDraftSuperseded, type ActionInlineDraft } from '../inline-drafts'
import { useActionInlineDraftController } from '../use-action-inline-draft-controller'
import { trackActionEvent } from '../track-action-event'

export interface ActionSaveSurface {
  /**
   * Persist the draft; failures land in `errorMessage` rather than throwing.
   * Resolves true once the draft persisted.
   */
  handleSave: () => Promise<boolean>
  handleDiscard: () => void
  clearError: () => void
  isBusy: boolean
  errorMessage: string | null
  isSuperseded: boolean
}

/**
 * The shared state machine behind every surface that resolves an unsent draft
 * — the dirty footer and the leave dialog: one controller wiring, one pair of
 * save/discard handlers, one error and superseded derivation, so the two
 * surfaces can't drift on how a draft resolves.
 */
export function useActionSaveSurface(action: StagedAction, draft: ActionInlineDraft | undefined): ActionSaveSurface {
  const { editOptions } = useStagedActionEditOptions({ actionId: action.id })
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const { discard, isUpdating, saveDraft } = useActionInlineDraftController({
    actionId: action.id,
    coreTitle: editOptions?.title?.coreTitle,
    serverContent: action.view?.content,
  })
  const isBusy = isSaving || isUpdating

  const clearError = useCallback(() => setSaveError(null), [])

  const handleDiscard = useCallback(() => {
    discard()
    setSaveError(null)
    trackActionEvent(action, 'discard-edit')
  }, [discard, action])

  const handleSave = useCallback(async () => {
    if (isSaving) return false
    setSaveError(null)
    setIsSaving(true)
    try {
      await saveDraft()
      return true
    } catch {
      setSaveError('Changes could not be saved')
      return false
    } finally {
      setIsSaving(false)
    }
  }, [isSaving, saveDraft])

  // The other half of the no-clobber guard: the draft overlay already keeps a
  // regeneration from repainting the editors, so when one lands underneath,
  // say so — Save keeps the user's version, Discard reveals the new draft.
  // Suppressed mid-save: the optimistic write shifts the content under the
  // still-held draft, which is resolution, not conflict.
  const isSuperseded = !isBusy && isDraftSuperseded(draft, action.view?.content)

  // A title that failed its own commit surfaces here too — it may be why the
  // surface is open at all — until Save retries it or Discard drops it.
  const errorMessage = saveError ?? draft?.titleError ?? null

  return { handleSave, handleDiscard, clearError, isBusy, errorMessage, isSuperseded }
}

/** Inline save-failure banner, identical on both surfaces. */
export function SaveErrorBanner({ message }: { message: string }) {
  return (
    <div className="bg-danger-subtle text-danger-subtle type-ui-xs corner-smooth rounded-lg px-3 py-2">{message}</div>
  )
}

/** The superseded-mid-edit warning — one copy of the copy. */
export function DraftSupersededWarning({ className }: { className?: string }) {
  return (
    <div className={twMerge('type-ui-sm flex min-w-0', className)}>
      <ActionWarning warning="Highlight updated this action while you were editing — Save keeps your version" />
    </div>
  )
}
